import { ScrollText, FileCheck, Shield, Building2, FileText, Gavel, Home, Users, Briefcase, Landmark } from "lucide-react";

/**
 * Icon lookup. The group's *data* — label, description, icon name — comes from the API; this maps
 * that name to a component, because a React component can't be sent over JSON.
 *
 * An allowlist rather than a dynamic import: the name arrives from admin-editable config, and a
 * page should not be able to break because someone typed an icon name that doesn't exist. An
 * unknown name simply draws a generic document.
 */
const ICONS = {
  scroll: ScrollText,
  "file-check": FileCheck,
  shield: Shield,
  building: Building2,
  file: FileText,
  gavel: Gavel,
  home: Home,
  users: Users,
  briefcase: Briefcase,
  landmark: Landmark,
};

/**
 * The document's *type* — the broad category, not the fifteen template titles beneath it.
 *
 * **Never returns a raw key.** `service_type` is a snake_case identifier, and several places used
 * to fall back to printing it, so a customer could be shown "contract_review" as the kind of
 * thing they had bought. An unknown key is de-underscored and title-cased instead, which means a
 * group added later reads acceptably before anyone adds it here.
 */
const TYPE_LABELS = {
  rental_agreement: "Rental Agreement",
  affidavit: "Affidavit",
  contract_review: "Contract Review",
  property_verification: "Property Verification",
};

export const prettyType = (serviceType) =>
  TYPE_LABELS[serviceType] ||
  String(serviceType || "Document").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

export const ICON_NAMES = Object.keys(ICONS);
export const groupIcon = (g) => ICONS[typeof g === "string" ? g : g?.icon] || FileText;
export const groupDesc = (g) => (typeof g === "string" ? "" : g?.description || "");

export const groupLink = (key, templates) => {
  const mine = (templates || []).filter(t => (t.group || "affidavit") === key && t.enabled !== false);
  if (mine.length > 1) return key === "affidavit" ? "/customer/affidavits" : `/customer/documents/${key}`;
  if (mine.length === 1) return `/customer/wizard/${key}?type=${mine[0].key}`;
  return `/customer/wizard/${key}`;
};

/** Price shown on a group card: its own rate, else the cheapest template within it. */
export const groupPrice = (key, pricing, templates) => {
  if (pricing?.[key]?.total != null) return pricing[key].total;
  const totals = (templates || [])
    .filter(t => (t.group || "affidavit") === key)
    .map(t => pricing?.[t.key]?.total)
    .filter(v => v != null);
  return totals.length ? Math.min(...totals) : null;
};

/**
 * Whether a group has anything a customer can actually order.
 *
 * Groups come from config and know nothing about templates, so a group whose only template was
 * switched off still rendered a card — which then led to a wizard for a document that is not on
 * sale. Upload services (no templates by design) are always orderable.
 */
export const groupIsOrderable = (key, templates, uploadServices = ["contract_review", "property_verification"]) =>
  uploadServices.includes(key) ||
  (templates || []).some(t => (t.group || "affidavit") === key && t.enabled !== false);
